import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { DataGridPro, GridRow } from '@mui/x-data-grid-pro';
import CircularProgress from '@mui/material/CircularProgress';
import CompSearch, { compSearchMuniCode } from './CompSearch';
import axiosInstance from '../../fetchConfig/axiosConfig';
import { specialFormatters } from '../../common/mappings';
import { calculateDistance } from '../../common/dateUtils';
import { processRowData } from './services/rowConstructionService';
import { generateErrorFlags, generateErrorCodes, generateErrorMessages } from '../../utils/compErrorFlagging';
import { normalizeCompId } from './common/compSheetLookup';
import CloseIcon from '@mui/icons-material/Close';
import {calculateHeaders} from '../../common/CalculateHeaderFlex';
import { usePersistedDataStore, useCompStore } from '../store.js';
import Tooltip from '@mui/material/Tooltip';
import { createColumnDefinitions, COLUMN_ORDER } from './config/columnDefinitions';
import { useColumnLayout, getDefaultColumnVisibility } from './config/columnVisibility';
import { AnomalyDetector, useAnomalyContext } from './AnomalyDetector';
import SharedGridToolbar from './SharedGridToolbar';
import { useUser } from '../UserContext';
import logger from '../../utils/logger';
import { useRarHistory } from './utils/calculatedTav';

const EMPTY_USER_FILTER_SETTINGS = {};
// Negotiation columns are always built as a sale run.
const DEFAULT_COLUMN_VISIBILITY = getDefaultColumnVisibility(true);

const numericValue = (value, defaultValue = '') => (value === 0 || value === '0' ? 0 : (value || defaultValue));

const shapeTownCompRow = (rowData, subject, negotiationObj) => {
  const processedRow = {
    ...rowData,
    parcel_id: rowData.parcel_id ?? rowData.ParcelID ?? rowData.PID ?? '',
    TransactionID: rowData.TransactionID ?? rowData.trans_id ?? null,
    Latitude: rowData.Latitude ?? rowData.latitude ?? null,
    Longitude: rowData.Longitude ?? rowData.longitude ?? null,
    Bath: rowData.Bath ?? rowData.BathsFull ?? rowData.fullBaths ?? '',
    HalfBath: rowData.HalfBath ?? rowData.BathsHalf ?? rowData.halfBaths ?? '',
    TotalGarageCapacity: rowData.TotalGarageCapacity ?? rowData.GarageCapacity ?? '',
  };
  processedRow.ParcelID = processedRow.parcel_id;
  processedRow.id = processedRow.parcel_id;
  processedRow.PropertyAssessment = processedRow.AssessmentTown;
  let distance = 0;
  if (subject?.Latitude && subject?.Longitude && processedRow.Latitude && processedRow.Longitude) {
    distance = calculateDistance(
      subject.Latitude,
      subject.Longitude,
      processedRow.Latitude,
      processedRow.Longitude
    ) || 0;
  }
  processedRow.distance = distance;
  processedRow.Distance = distance;
  const mappedRow = processRowData(processedRow, specialFormatters, numericValue);
  const errorFlags = generateErrorFlags(mappedRow, negotiationObj);
  Object.assign(mappedRow, errorFlags);
  const errorCodes = generateErrorCodes(errorFlags);
  if (errorCodes.length > 0) {
    mappedRow.errors = errorCodes;
    mappedRow.errorMessages = generateErrorMessages(errorCodes);
  }
  return mappedRow;
};

// Inner component that uses anomaly detection
const TownEvidenceTableInner = ({
  rows,
  subject,
  updateNegotiationComps,
  handleSetPropertyInfo,
  unsavedChangesRef,
  handleDeleteProperty,
  headers,
  tableProps,
  handleAddressClick,
  viewCallback,
  addNegotiationComp,
  negotiationCompsTouched = false,
  markNegotiationCompsTouched,
  townCompParcelIds = [],
  // Props needed for adj_price tooltips
  memoizedSelectedAdjustments,
  savedComps,
  memoizedCompObject,
  caseObject,
  dataGridStyles,
  updateRefWithSubj,
  isSettled = false,
  setIsPinned,
  isPinned = false,
  setIsSavingCallback,
  handleSaveComps,
  saveChanges,
  discardChanges,
  handleToggleReordering,
  reordering,
  reOrderingRef,
  userReordered,
  compNumber,
  isSaving,
  // Column visibility props
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnWidths,
  columnLayout,
}) => {
  const { user } = useUser();
  const {
    hasFieldAnomalies,
    getFieldAnomalies,
    getAnomalyCellStyle,
    getAnomalyTooltip,
    updateVisibilityForAnomalies,
  } = useAnomalyContext();

  
  const getAdjustments = useCompStore((state)=> state.adjustments)
  const getEvalDate = usePersistedDataStore((state) => state.evalDate);
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const rarHistory = useRarHistory(getNegotiationObj?.MuniCode);
  const compMuniCode = compSearchMuniCode(getNegotiationObj?.MuniCode, subject?.parcel_id);
  const reportType = usePersistedDataStore((state) => state.reportType);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const attemptedTownIdsRef = useRef(new Set());
  const townLoadKeyRef = useRef('');

  const townNegotiationComps = useMemo(
    () =>
      rows
        .filter((row) => {
          if (row?.isSubject) return false;
          const addr = row?.Address ?? row?.PropertyInfo?.Address ?? '';
          return String(addr).trim() !== '';
        })
        .map((row) => row.parcel_id)
        .filter((pid) => pid != null && pid !== ''),
    [rows]
  );

  const saveNegotiationTownComps = useCallback(
    (parcelIds) => handleSaveComps(parcelIds, { townNegotiationCompsSave: true }),
    [handleSaveComps]
  );

  // Update visibility for anomalous fields
  useEffect(() => {
    updateVisibilityForAnomalies();
  }, [updateVisibilityForAnomalies]);

  useEffect(() => {
    const loadKey = (townCompParcelIds || []).map((id) => normalizeCompId(id)).filter(Boolean).join('|');
    if (townLoadKeyRef.current !== loadKey) {
      townLoadKeyRef.current = loadKey;
      attemptedTownIdsRef.current = new Set();
    }
    if (negotiationCompsTouched || !townCompParcelIds?.length || !getNegotiationObj?.TaxYear) {
      setLoadingInventory(false);
      return;
    }
    const isSaleRun = String(reportType || getNegotiationObj?.reportType || '').toLowerCase() !== 'assessment';
    const pending = townCompParcelIds.filter((savedId) => {
      const needle = normalizeCompId(savedId);
      if (!needle || attemptedTownIdsRef.current.has(needle)) return false;
      return !rows.some(
        (row) =>
          row.id !== 'new-comp' &&
          (normalizeCompId(row?.parcel_id) === needle || normalizeCompId(row?.TransactionID) === needle)
      );
    });
    if (!pending.length) {
      setLoadingInventory(false);
      return;
    }
    let cancelled = false;
    setLoadingInventory(true);
    (async () => {
      try {
        const lookups = pending.map((savedId) => {
          const raw = String(savedId).trim();
          const byTxn = isSaleRun && !/^[A-Za-z]/.test(raw);
          const txn = normalizeCompId(raw);
          return byTxn
            ? { key: `t:${txn}`, parcel_id: null, transaction_id: txn }
            : { key: `p:${raw}`, parcel_id: raw, transaction_id: null };
        });
        const { data } = await axiosInstance.post('/lookup_property_inventory_batch', {
          lookups,
          year: getNegotiationObj.TaxYear,
        });
        if (cancelled) return;
        const mapped = lookups.map((lookup) => {
          const raw = data?.[lookup.key]?.[0];
          if (!raw) {
            logger.warn('[TownEvidenceTable] no inventory row', lookup);
            return null;
          }
          const row = shapeTownCompRow(
            { ...raw, TransactionID: raw.TransactionID ?? raw.trans_id ?? lookup.transaction_id ?? null },
            subject,
            getNegotiationObj
          );
          return row?.parcel_id ? row : null;
        }).filter(Boolean);
        if (mapped.length) updateNegotiationComps(mapped);
      } catch (error) {
        logger.error('[TownEvidenceTable] inventory batch failed', error);
      } finally {
        if (!cancelled) {
          pending.forEach((savedId) => attemptedTownIdsRef.current.add(normalizeCompId(savedId)));
          setLoadingInventory(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    negotiationCompsTouched,
    townCompParcelIds,
    rows,
    updateNegotiationComps,
    subject,
    getNegotiationObj,
    reportType,
  ]);

  // Create generic headers for negotiation view
  const createGenericNegotiationHeaders = useCallback(() => {
    return createColumnDefinitions({
      handleSelectAllChange: () => {}, // Not used in negotiation
      handleAddressClick: handleAddressClick || (() => {}),
      IS_SALE_RUN: true, // Negotiation is always sale run
      getTimeAdjustmentSettings: () => ({}),
      getEvalDate: getEvalDate || getNegotiationObj?.evalDate,
      taxYear: getNegotiationObj?.TaxYear,
      rarHistory,
      memoizedSelectedAdjustments: memoizedSelectedAdjustments || {},
      caseObject: caseObject || {},
      getAdjustments: getAdjustments || (() => ({})),
      savedComps: savedComps || [],
      memoizedCompObject: memoizedCompObject || {},
      handleAddAdj: () => {},
      handleDeleteAdj: () => {}
    }).map((header) => ({ ...header, ...columnWidths?.[header.field] }));
  }, [handleAddressClick, memoizedSelectedAdjustments, caseObject, getAdjustments, savedComps, memoizedCompObject, getEvalDate, getNegotiationObj?.evalDate, getNegotiationObj?.TaxYear, rarHistory, columnWidths]);

  // Special getRowClassName for negotiation view - no old error styling (handled by anomaly detection)
  const getNegotiationRowClassName = useCallback((params) => {
    // Add safety check
    if (!params.row) return '';
    let classNames = [];
    
    // Disable old error border styling - now handled by anomaly detection
    // For negotiation view, we don't want the red/green background colors
    return classNames.join(' ');
  }, []);

  // Get cell className for anomaly styling - memoize with stable dependencies
  const getCellClassName = useCallback((params) => {
    if (!params.row) return '';
    
    // Skip blank rows (rows without addresses) - these are for adding new comps
    const isBlankRow = !params.row.Address || params.row.Address === '' || 
                      (params.row.PropertyInfo && (!params.row.PropertyInfo.Address || params.row.PropertyInfo.Address === ''));
    if (isBlankRow) return '';
    
    // Always check for anomalies based on current data
    const hasAnomalies = hasFieldAnomalies(params.row.id, params.field);
    if (hasAnomalies) {
      const fieldAnomalies = getFieldAnomalies(params.row.id, params.field);
      const hasError = fieldAnomalies.some(anomaly => anomaly.severity === 'error');
      return hasError ? 'anomaly-cell-error' : 'anomaly-cell-warning';
    }
    
    // Return empty string to clear any existing anomaly classes
    return '';
  }, [hasFieldAnomalies, getFieldAnomalies]);


  // Create negotiation-specific columns by extending generic columns
  const createNegotiationColumns = useCallback((genericHeaders, addNegotiationComp, updateNegotiationComps, handleDeleteProperty) => {
    return genericHeaders.map((header) => {
      const isPropertyInfo = header.field === 'Address';
      const isActions = header.field === 'actions';
      const isSalePrice = header.field === 'SalePrice';
      const isAdjPrice = header.field === 'adj_price';

      if (isActions) {
        return {
          ...header,
          field: 'actions',
          headerName: 'Remove',
          width: 65,
          sortable: false,
          renderCell: (params) => {
            const hasAddress = params.row?.Address !== '';

            return (
              <div 
                className="relative text-center h-full group flex items-center justify-center"
                onClick={(e) => {
                  // Only allow clicks if there's an address
                  if (!hasAddress) {
                    e.stopPropagation();
                  }
                }}
              >
                {hasAddress ? (
                  <CloseIcon
                    className="text-red-500 font-bold cursor-pointer"
                    onClick={() => handleDeleteProperty(params.row)}
                  />
                ) : (
                  <div style={{ color: '#9e9e9e', cursor: 'default' }}>—</div>
                )}
              </div>
            );
          },
        };
      }

      return {
        ...header,
        ...(isPropertyInfo && { minWidth: 180 }),
        renderCell: (params) => {
          const isAddressBlank = params.row?.Address === '' || false;

          if (isPropertyInfo) {
            if (isAddressBlank) {
              return (
                <div className="h-full items-center" onKeyDown={(e) => e.stopPropagation()}>
                  <CompSearch
                    compSelectCallback={async (suggestion) => {
                      // logger.log('CompSearch callback in negotiation view:', suggestion);
                      if (suggestion.parcel_id) {
                        markNegotiationCompsTouched?.();
                        // logger.log('Adding negotiation comp:', suggestion.parcel_id);
                        // Use the new addNegotiationComp function
                        const newRow = await addNegotiationComp(suggestion.parcel_id);
                        // logger.log('addNegotiationComp returned:', newRow);
                        // Add it to negotiation comps
                        if (newRow) {
                          updateNegotiationComps(newRow);
                        }
                      }
                    }}
                    placeholderText="PID or Address"
                    restrictToState="NY"
                    muniCode={compMuniCode}
                  />
                </div>
              );
            } else {
              return (
                <span
                  onClick={() => handleAddressClick && handleAddressClick(params.row)}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  className="flex-grow"
                >
                  {params.row.Address}
                </span>
              );
            }
          }

          if (isSalePrice) {
            const value = params.value;
            if (value === 0 || value === '') {
              return <div style={{ color: 'transparent' }}>—</div>;
            }
            
            // Check for anomalies in this field
            const hasAnomalies = hasFieldAnomalies(params.row.id, header.field);
            const anomalyStyle = hasAnomalies ? getAnomalyCellStyle(params.row.id, header.field) : {};
            const anomalyTooltip = hasAnomalies ? getAnomalyTooltip(params.row.id, header.field) : null;
            
            const content = Number(value).toLocaleString();
            
            // Wrap with anomaly styling and tooltip if needed
            if (hasAnomalies) {
              return (
                <Tooltip title={anomalyTooltip} arrow placement="top">
                  <span style={anomalyStyle}>
                    {content}
                  </span>
                </Tooltip>
              );
            }
            
            return content;
          }

          // For adj_price, let the generic renderCell handle it (includes tooltips)
          // but handle blank rows
          if (isAdjPrice && isAddressBlank) {
            return <div style={{ color: 'transparent' }}>—</div>;
          }

          if (isAddressBlank) {
            return <div style={{ color: 'transparent' }}>—</div>;
          }

          // Check for anomalies in this field
          const hasAnomalies = hasFieldAnomalies(params.row.id, header.field);
          const anomalyStyle = hasAnomalies ? getAnomalyCellStyle(params.row.id, header.field) : {};
          const anomalyTooltip = hasAnomalies ? getAnomalyTooltip(params.row.id, header.field) : null;

          // Use the original renderCell if it exists (this includes adj_price tooltips)
          if (header.renderCell) {
            const originalContent = header.renderCell(params);
            
            // Wrap with anomaly styling and tooltip if needed
            if (hasAnomalies) {
              return (
                <Tooltip title={anomalyTooltip} arrow placement="top">
                  <div style={anomalyStyle}>
                    {originalContent}
                  </div>
                </Tooltip>
              );
            }
            return originalContent;
          }

          // Default rendering logic for columns without custom renderCell
          const value = params.value;
          let content;

          // Special-case fields that should not show numeric 0 and should not be number-formatted
          if (header.field === 'MLSNumber' || header.field === 'View') {
            if (value === 0 || value === '0' || value === '' || value === null || typeof value === 'undefined') {
              content = '';
            } else {
              content = String(value);
            }
          } else if (value instanceof Date) {
            content = value.toLocaleDateString();
          } else if (value !== '' && value !== null && typeof value !== 'undefined' && !isNaN(value)) {
            // Special handling for Acres - show 2 decimal places
            if (header.field === 'Acres') {
              content = Number(Number(value).toFixed(2)).toLocaleString() || '';
            } else {
              // Default handling for other numeric fields
              content = Number(Number(value).toFixed(0)).toLocaleString() || '';
            }
          } else {
            content = value || '';
          }

          // Wrap with anomaly styling and tooltip if needed
          if (hasAnomalies) {
            return (
              <Tooltip title={anomalyTooltip} arrow placement="top">
                <div style={anomalyStyle}>
                  {content}
                </div>
              </Tooltip>
            );
          }

          return content;
        },
      };
    });
  }, [hasFieldAnomalies, getAnomalyCellStyle, getAnomalyTooltip, handleAddressClick, markNegotiationCompsTouched, compMuniCode]);

  // Get negotiation columns - memoize with stable dependencies
  const negotiationColumns = useMemo(() => {
    const genericHeaders = createGenericNegotiationHeaders();
    return createNegotiationColumns(genericHeaders, addNegotiationComp, updateNegotiationComps, handleDeleteProperty);
  }, [
    addNegotiationComp, 
    updateNegotiationComps, 
    handleDeleteProperty,
    createGenericNegotiationHeaders,
    createNegotiationColumns,
  ]);




  // Create filtered headers based on column visibility model (like regular view)
  const filteredHeaders = useMemo(() => {
    return negotiationColumns.filter((header) => {
      return columnVisibilityModel[header.field] !== false;
    });
  }, [negotiationColumns, columnVisibilityModel]);

  // Calculate flex for headers (like regular view)
  const calculateFlexForHeaders = useCallback(() => {
    const containerWidth = document.documentElement.clientWidth - 50;
    return calculateHeaders(filteredHeaders, columnVisibilityModel, containerWidth);
  }, [filteredHeaders, columnVisibilityModel]);

  // // just directly call on component load, was having issues w/ passing down correct flex to subject row.
  const headerResult = useMemo(() => calculateFlexForHeaders(), [calculateFlexForHeaders]);

  // Memoize stable props to prevent unnecessary re-renders
  const stableRowsPerPageOptions = useMemo(() => [5], []);
  
  const stableGetRowId = useCallback(
    (row) => row?.id ?? (row?.parcel_id != null
      ? `${row.parcel_id}${row.TransactionID != null && row.TransactionID !== '' ? `-${row.TransactionID}` : ''}`
      : 'new-comp'),
    [],
  );
  
  const stableSx = useMemo(() => ({
    ...dataGridStyles,
    '& .MuiDataGrid-cell.anomaly-cell-error': {
      border: '2px solid #d32f2f !important',
      backgroundColor: '#ffebee !important',
      '&:hover': {
        border: '2px solid #d32f2f !important',
        backgroundColor: '#ffebee !important',
      },
    },
    '& .MuiDataGrid-cell.anomaly-cell-warning': {
      border: '2px solid #f57c00 !important',
      backgroundColor: '#fff3e0 !important',
      '&:hover': {
        border: '2px solid #f57c00 !important',
        backgroundColor: '#fff3e0 !important',
      },
    },
  }), [dataGridStyles]);

  // Memoize row component separately to prevent unnecessary re-renders
  const RowComponent = useCallback((rowProps) => {
    try {
      const rowId = rowProps?.id ?? rowProps?.row?.id ?? rowProps?.row?.parcel_id;
      const rowExists = rows.some((r) => (r?.id ?? r?.parcel_id) === rowId);
      
      // If row doesn't exist, don't render it
      if (!rowExists) {
        return null;
      }
      
      // Disable old error tooltips - now handled by anomaly detection
      return <GridRow {...rowProps} />;
    } catch (error) {
      logger.warn('Error rendering row:', error, 'Row ID:', rowProps?.id ?? rowProps?.row?.id);
      return null;
    }
  }, [rows]);

  // Memoize toolbar component separately
  const ToolbarComponent = useCallback(() => (
    <SharedGridToolbar
      filteredHeaders={headerResult}
      subject={subject}
      updateRefWithSubj={updateRefWithSubj}
      handleSetPropertyInfo={handleSetPropertyInfo}
      unsavedChangesRef={unsavedChangesRef}
      columnVisibilityModel={columnVisibilityModel}
      columnLayout={columnLayout}
      showSettlement={true}
      caseObject={caseObject}
      headers={headers}
      isSettled={isSettled}
      setIsPinned={setIsPinned}
      isPinned={isPinned}
      setIsSavingCallback={setIsSavingCallback}
      handleSaveComps={saveNegotiationTownComps}
      saveChanges={saveChanges}
      discardChanges={discardChanges}
      handleToggleReordering={handleToggleReordering}
      reordering={reordering}
      reOrderingRef={reOrderingRef}
      userReordered={userReordered}
      selectedRows={townNegotiationComps}
      useTownNegotiationCompsForSave
      townNegotiationComps={townNegotiationComps}
      userName={user?.userName ?? ''}
      compNumber={compNumber}
      isSaving={isSaving}
    />
  ), [
    subject,
    updateRefWithSubj,
    handleSetPropertyInfo,
    unsavedChangesRef,
    headerResult,
    columnVisibilityModel,
    columnLayout,
    caseObject,
    headers,
    isSettled,
    setIsPinned,
    isPinned,
    setIsSavingCallback,
    saveNegotiationTownComps,
    saveChanges,
    discardChanges,
    handleToggleReordering,
    reordering,
    reOrderingRef,
    userReordered,
    townNegotiationComps,
    user?.userName,
    compNumber,
    isSaving,
  ]);

  const stableSlots = useMemo(() => ({
    row: RowComponent,
    toolbar: ToolbarComponent,
  }), [RowComponent, ToolbarComponent]);
  
  return (
    <div className="w-full h-full">
      {/* Data Grid */}
      <div style={{ height: 450, width: '100%', position: 'relative' }} className="overflow-visible">
        {loadingInventory && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/75">
            <CircularProgress size={28} />
            <div className="mt-2 text-sm text-gray-700">Loading Town Comp Inventory</div>
          </div>
        )}
        <DataGridPro
          {...tableProps}
          key={`negotiation-table-${rows.length}-${rows.map(r => r.id).join('-')}`}
          rows={rows}
          hideFooter
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          columns={negotiationColumns}
          pageSize={5}
          rowsPerPageOptions={stableRowsPerPageOptions}
          disableRowSelectionOnClick
          getRowId={stableGetRowId}
          getRowClassName={getNegotiationRowClassName}
          getCellClassName={getCellClassName}
          sx={stableSx}
          slots={stableSlots}
          showToolbar
          disableRowSelectionExcludeModel
        />
      </div>
    </div>
  );
};

const TownEvidenceTable = (props) => {
  const [anomalyColumnVisibility, setAnomalyColumnVisibility] = useState({});
  const { columnVisibilityModel, onColumnVisibilityModelChange, columnWidths, columnLayout } = useColumnLayout('caseReview', COLUMN_ORDER, DEFAULT_COLUMN_VISIBILITY, anomalyColumnVisibility);
  // The detector only turns columns on; keep those outside the user's toggles so they never mark the view dirty.
  const showAnomalyColumns = useCallback((model) => setAnomalyColumnVisibility((prev) => {
    const shown = Object.keys(model).filter((field) => model[field] && columnVisibilityModel[field] === false && !prev[field]);
    return shown.length ? { ...prev, ...Object.fromEntries(shown.map((field) => [field, true])) } : prev;
  }), [columnVisibilityModel]);

  return (
    <AnomalyDetector
      rows={props.rows}
      subject={props.subject}
      columnVisibilityModel={columnVisibilityModel}
      setColumnVisibilityModel={showAnomalyColumns}
      userFilterSettings={props.userFilterSettings ?? EMPTY_USER_FILTER_SETTINGS}
      getAdjustments={props.getAdjustments}
    >
      <TownEvidenceTableInner
        {...props}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        columnWidths={columnWidths}
        columnLayout={columnLayout}
      />
    </AnomalyDetector>
  );
};

// Memoize the TownEvidenceTable component
export default React.memo(TownEvidenceTable);
